import type { MediaBaseResponse } from '@/types/media.types';
import { WorkOrderVendor } from './workOrder.types';
import { VendorResponse } from './vendor.types';

/* ----------------------------- Constants ----------------------------- */
export const MAX_MEDIA_LENGTH_ATTEMPT = 50;

export const serviceFieldMap: Record<string, string> = {
  personAccepting: 'Person Accepting',
  relation: 'Title/Relation to Servee',
  age: 'Age',
  height: 'Height',
  weight: 'Weight',
  hair: 'Hair',
  sex: 'Sex',
  race: 'Race',
};

export enum ATTEMPT_TYPE {
  PERSONAL = 'PERSONAL',
  SUB = 'SUB',
  BY_MAIL = 'BY_MAIL',
  BY_EMAIL = 'BY_EMAIL',
  ATTEMPTED = 'ATTEMPTED',
  NOT_SERVED = 'NOT_SERVED',
  POSTED = 'POSTED',
}

export enum ATTEMPT_TYPE_LABEL {
  PERSONAL = 'Personal',
  SUB = 'Substitute',
  BY_MAIL = 'Served by Mail',
  BY_EMAIL = 'Served by E-mail',
  ATTEMPTED = 'Attempted',
  NOT_SERVED = 'Not Served',
  POSTED = 'Posted',
}

export const MAX_IMAGE_LENGTH_ATTEMPT = 50;

/* ----------------------------- Types ----------------------------- */
export interface AttemptParams {
  workOrderId: string;
}

export interface UserResponse {
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface WorkOrderAttemptMedia {
  id: string;
  workOrderAttemptId: string;
  mediaId: string;
  media?: MediaBaseResponse;
  attemptMediaId: string;
}

export interface WorkOrderAttempt {
  attemptId: string;
  workOrderId: string;
  dateOfAttempt: Date;
  description?: string | null;
  createdAt?: string;
  updatedAt?: string;
  type: ATTEMPT_TYPE;
  updatedBy?: UserResponse | null;
  createdBy?: UserResponse | null;
  personAccepting?: string | null;
  relation?: string | null;
  age: number | null;
  height: number | null;
  weight: number | null;
  hair?: string | null;
  sex?: string | null;
  race?: string | null;
  otherDetails?: string | null;
  workOrderAttemptMedia: WorkOrderAttemptMedia[];
  serverId?: string;
  vendorId?: string;
  server?: UserResponse;
  vendor?: VendorResponse;
  emailSentDate?: Date;
  emailAcknowledgementDate?: Date;
  emailAddress?: string;
  trackingNumber?: string;
  remarks?: string;
  additionalInformation?: string;
}

/** Vacancy checklist on linked HI attempts returned by PS attempt GET enrichment. */
export interface HomeInspectionAttemptVacancyChecklist {
  vacancyChecklistId?: string;
  homeInspectionAttemptId?: string;
  overgrownOrNeglectedVegetation?: boolean | null;
  hazardousNoxiousOrUnhealthySubstances?: boolean | null;
  newspapersCircularsFlyersOrMail?: boolean | null;
  utilitiesDisconnected?: boolean | null;
  junkLitterTrashOrDebris?: boolean | null;
  noWindowTreatments?: boolean | null;
  noFurnishingsOrPersonalItems?: boolean | null;
  neighborStatements?: boolean | null;
  neighborStatementsSummary?: string | null;
  boardedOrBrokenWindows?: boolean | null;
  brokenOrUnlockedDoors?: boolean | null;
  riskToHealthSafetyOrWelfare?: boolean | null;
  riskToHealthSafetyOrWelfareDetails?: string | null;
  codeViolationOrVacancyOrder?: boolean | null;
  winterizedByMortgagee?: boolean | null;
  writtenStatementOfAbandonment?: boolean | null;
  otherIndiciaOfAbandonment?: boolean | null;
  otherIndiciaOfAbandonmentDetails?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** HI attempt shape nested on PS WorkOrderAttemptGet when the WO is linked. */
export interface HomeInspectionAttemptPsView {
  attemptId: string;
  workOrderId: string;
  dateOfAttempt: string;
  timeOfAttempt: string;
  ownerAddressLine1: string | null;
  ownerAddressLine2: string | null;
  ownerCity: string | null;
  ownerState: string | null;
  ownerZipCode: string | null;
  ownerPhone: string | null;
  maritalStatus: 'MARRIED' | 'SINGLE' | null;
  spouseName: string | null;
  ownerOccupied: 'YES' | 'NO' | null;
  vacant: 'YES' | 'NO' | null;
  bothOwnersResideTogether: 'YES' | 'NO' | null;
  coOwnerName: string | null;
  coOwnerAddressLine1: string | null;
  coOwnerAddressLine2: string | null;
  coOwnerCity: string | null;
  coOwnerState: string | null;
  coOwnerZipCode: string | null;
  defendantInArmedForces: 'YES' | 'NO' | null;
  tenantOccupied: 'YES' | 'NO' | null;
  relative: 'YES' | 'NO' | null;
  lease: 'YES' | 'NO' | null;
  rentAmount: number | string | null;
  houseExterior: string;
  houseExteriorOther: string | null;
  houseType: string;
  numberOfUnits: number;
  garage: string;
  sale: string;
  saleBy: string | null;
  salePhone: string | null;
  saleDate: string | null;
  comments: string | null;
  inspectorId: string | null;
  createdById: string;
  updatedById: string | null;
  createdAt: string;
  updatedAt: string;
  tenants: { tenantId: string; attemptId: string; name: string; floor: string | null }[];
  media: {
    attemptMediaId: string;
    attemptId: string;
    mediaId: string;
    createdAt: string;
    media?: {
      mediaId: string;
      type: string;
      mimeType: string;
      path: string;
      bucketName: string;
      baseUrl: string;
      createdAt: string;
    };
  }[];
  vacancyChecklist?: HomeInspectionAttemptVacancyChecklist | null;
}

export interface WorkOrderAttemptListResponse {
  number: string;
  workOrderId: string;
  server: {
    userId: string;
    firstName: string;
    lastName: string;
  };
  vendor?: WorkOrderVendor;
  workOrderAttempts: WorkOrderAttempt[];
  /** Present when the Process Service work order is linked to Home Inspection. */
  homeInspectionAttempts?: HomeInspectionAttemptPsView[];
}
export interface GetAttemptImagesResponseById {
  attemptId: string;
  images: {
    mediaId: string;
    imageBuffer: string;
    mimeType: string;
  }[];
}

/* ----------------------------- Input Types ----------------------------- */
export interface BaseFieldsCreate {
  dateOfAttempt: Date;
  description?: string | null;
  type: ATTEMPT_TYPE;
  attemptId?: string;
  mediaIds?: string[];
}

export interface BaseFieldsUpdate {
  attemptId: string;
  dateOfAttempt?: Date;
  description?: string | null;
  type?: ATTEMPT_TYPE;
  mediaIds?: {
    create?: string[];
    delete?: string[];
  };
  additionalInformation?: string | null;
}

export interface ServiceFields {
  personAccepting?: string | null;
  relation?: string | null;
  age?: number | null;
  height?: number | null;
  weight?: number | null;
  hair?: string | null;
  sex?: string | null;
  race?: string | null;
  otherDetails?: string | null;
}

export interface WorkOrderAttemptCreateRequest extends BaseFieldsCreate, ServiceFields {
  additionalInformation?: string | null;
  emailSentDate?: Date | null;
  emailAcknowledgementDate?: Date | null;
  emailAddress?: string | null;
  trackingNumber?: string | null;
  remarks?: string | null;
  /** Nested HI attempt for first create on a PS WO linked to HI (date/time come from PS attempt). */
  homeInspectionAttempt?: Omit<
    import('@/pages/homeInspection/hiAttempt.schema').HiAttemptCreatePayload,
    'dateOfAttempt' | 'timeOfAttempt'
  > | null;
}

export interface WorkOrderAttemptUpdateRequest extends BaseFieldsUpdate, ServiceFields {}

export interface WorkOrderAttemptPatchRequest {
  create?: WorkOrderAttemptCreateRequest[];
  update?: WorkOrderAttemptUpdateRequest[];
}
export interface ImageType {
  id: string;
  name: string;
  image: File | null | undefined;
  mediaId?: string | null;
  mediaData?: MediaBaseResponse | null;
}

export interface AttemptDetails {
  type: ATTEMPT_TYPE;
  attemptId?: string;
  workOrderId?: string;
  serverName?: string;
  dateOfAttempt: Date;
  timeOfAttempt?: Date | null;
  description?: string | null;
  additionalInformation?: string | null;
  personAccepting?: string | null;
  relation?: string | null;
  age?: number | null;
  height?: string | null;
  weight?: number | null;
  hair?: string | null;
  sex?: string | null;
  race?: string | null;
  otherDetails?: string | null;
  emailAddress?: string | null;
  emailSentDate?: Date | null;
  emailAcknowledgementDate?: Date | null;
  remarks?: string | null;
  trackingNumber?: string | null;
  createdById?: string | null;
  updatedById?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  deletedAt?: string | null;
  images?: ImageType[];
  workOrderMedia?: WorkOrderAttemptMedia[];
}
